import { ITranslateMethod } from '@core/js/hooks/i18n/types';
import { confirm, email, fn, length, regexp, required } from '@core/js/utils/Validation/validationRules';

import { VALIDATION_FIELDS } from '../constants';
import { TGetValidationRules } from '../types';

export const getPasswordCriteria = (t: ITranslateMethod) => [
    {
        fn(value: string) {
            return /[a-zA-Z]/gi.test(value) && /[0-9]/g.test(value);
        },
        label: 'playtech.mobile.system.registration.validation.password.label.lettersNumbers',
        error: t('password.message.lettersNumbers')
    },
    {
        fn(value: string) {
            return /[A-Z]/g.test(value);
        },
        label: 'playtech.mobile.system.registration.validation.password.label.uppercase',
        error: t('password.message.uppercase')
    },
    {
        fn(value: string) {
            return /[a-z]/g.test(value);
        },
        label: 'playtech.mobile.system.registration.validation.password.label.lowercase',
        error: t('password.message.lowercase')
    },
    {
        fn(value: string) {
            return value.length >= 8;
        },
        label: 'playtech.mobile.system.registration.validation.password.label.length',
        error: t('password.message.length')
    }
];

export const getValidationRules = (t: ITranslateMethod): TGetValidationRules => ({
    email: [
        required({ error: t('email.required') }),
        length({ min: 6, max: 100, error: t('email.length') }),
        email({ error: t('email.regex') })
    ],
    gender: [required({ error: t('gender-required') })],
    firstname: [
        required({ error: t('firstName-required') }),
        length({ min: 2, max: 30, error: t('firstName-length') }),
        regexp({ re: /^[a-zA-Z\s\-‘’']+$/, error: t('firstName-regexp') })
    ],
    lastname: [
        required({ error: t('lastName-required') }),
        length({ min: 2, max: 30, error: t('lastName-length') }),
        regexp({ re: /^[a-zA-Z\s\-‘’']+$/, error: t('lastName-regexp') })
    ],
    dateOfBirth: [
        required({ error: t('date-regexp') }),
        fn({
            fn: (value = '') => {
                const inputString = value
                    .split('-')
                    .map(num => Number(num));

                if (inputString.length !== 3) {
                    return [t('date-regexp')];
                }

                // For default input date
                if (inputString[0].toString().length === 4) {
                    inputString.reverse();
                }

                const [day, month, year] = inputString;
                const checkDate = new Date(year, month - 1, day);

                if (
                    year.toString().length !== 4
                    || checkDate.getFullYear() !== year
                    || checkDate.getMonth() !== month - 1
                    || checkDate.getDate() !== day
                ) {
                    return [t('date-regexp')];
                }

                const adultDate = new Date(year + 18, month - 1, day);

                if (adultDate > new Date()) {
                    return [t('date-fn')];
                }
            }
        })
    ],
    membershipNumber: [
        required({ error: t('membership.required') }),
        regexp({ re: /^[0-9]+$/, error: t('membership.regexp') }),
        length({ min: 10, max: 10, error: t('membership.length') })
    ],
    pin: [
        required({ error: t('pin.required') }),
        length({ min: 4, max: 6, error: t('pin.length') }),
        regexp({ re: /^[0-9]+$/, error: t('pin.regexp') })
    ],
    confirmPin: [
        required({ error: t('confirmPin.required') }),
        confirm({ field: VALIDATION_FIELDS.pin, error: t('confirmPin.fn') })
    ],
    pinReg: [
        required({ error: t('pinReg.required') }),
        length({ min: 4, max: 6, error: t('pinReg.length') }),
        regexp({ re: /^[0-9]+$/, error: t('pinReg.regexp') })
    ],
    confirmPinReg: [
        required({ error: t('confirmPinReg.required') }),
        confirm({ field: VALIDATION_FIELDS.pinReg, error: t('confirmPinReg.fn') }),
        regexp({ re: /^[0-9]+$/, error: t('confirmPinReg.regexp') })
    ],
    password: [
        required({ error: t('password.required') }),
        length({ min: 8, max: 64, error: t('password.length') }),
        regexp({ re: /^[A-Za-z0-9@,.!"£$%^&*#?+-_=|;:~€]+$/, error: t('password.regexp') }),
        fn({
            fn: (value: string) => {
                const passwordCriteria = getPasswordCriteria(t);
                for (let i = 0; i < passwordCriteria.length; i++) {
                    const { fn: fnPassword, error } = passwordCriteria[i];

                    if (!fnPassword(value)) {
                        return [error];
                    }
                }
            }
        })
    ],
    confirmPassword: [
        required({ error: t('confirmPassword.required') }),
        confirm({ field: 'password', error: t('confirmPassword.fn') }),
        regexp({ re: /^[A-Za-z0-9@,.!"£$%^&*#?+-_=|;:~€]+$/, error: t('confirmPassword.regexp') })
    ],
    oldPassword: [
        required()
    ],
    verificationQuestion: [required({ error: t('verificationQuestion.required') })],
    verificationAnswer: [
        required({ error: t('verificationAnswer.required') }),
        length({ min: 1, max: 160, error: t('verificationAnswer.length') }),
        regexp({ re: /^[A-Za-z0-9\\/\s-]+$/, error: t('verificationAnswer.regexp') })
    ],
    zip: [
        required({ error: t('zip-required') }),
        length({ min: 1, max: 12, error: t('zip-length') }),
        regexp({ re: /^[A-Za-z0-9\s]+$/, error: t('zip-regexp') })
    ],
    house: [
        required({ error: t('houseno-required') }),
        length({ min: 1, max: 56, error: t('houseno-length') }),
        regexp({ re: /^[A-Za-z0-9.\-‘’'/&#\s]+$/, error: t('houseno-regexp') })
    ],
    street: [
        required({ error: t('address.required') }),
        length({ min: 2, max: 160, error: t('address.length') }),
        regexp({ re: /^[A-Za-z0-9.,‘’'#&/\s-]+$/, error: t('address.regexp') })
    ],
    address: [
        required({ error: t('address.required') }),
        length({ min: 2, max: 160, error: t('address.length') }),
        regexp({ re: /^[A-Za-z0-9.‘’'#&/\s-]+$/, error: t('address.regexp') })
    ],
    city: [
        required({ error: t('city.required') }),
        length({ min: 2, max: 50, error: t('city.length') }),
        regexp({ re: /^[A-Za-z0-9.\-\s‘’']+$/, error: t('city.regexp') })
    ],
    state: [length({ min: 0, max: 30, error: t('county.length') })],
    terms: [required({ predicate: (value: unknown) => Boolean(value), error: t('terms.required') })],
    promoCode: [
        required({ error: t('promoCode.required') }),
        length({ min: 1, max: 16, error: t('promoCode.length') }),
        regexp({ re: /^[A-Za-z0-9]+$/, error: t('promoCode.regexp') })
    ],
    postcode: [
        required({ error: t('postcode.required') }),
        length({ min: 1, max: 12, error: t('postcode.length') }),
        regexp({ re: /^[A-Za-z0-9\s]+$/, error: t('postcode.regexp') })
    ],
    userName: [required({ error: t('username.required') })],
    userNameRetail: [required({ error: t('userNameRetail.required') })],
    searchAddress: [],
    amount: [],
    occupation: [],
    duplicate: { email: [] },
    login: {
        password: [required({ error: t('password.required') })]
    },
    userdetails: {
        password: [required({ error: t('password.required') })]
    },
    slotReg: {
        // eslint-disable-next-line camelcase
        casino_contactByEmail: [
            fn({
                fn: (value: boolean, _: unknown, values: Record<string, string>) => {
                    if (!value && !values.casino_contactBySMS) {
                        return [t('contactByEmail.required')];
                    }
                }
            })
        ],
        // eslint-disable-next-line camelcase
        casino_contactBySMS: [
            fn({
                fn: (value: boolean, _: unknown, values: Record<string, string>) => {
                    if (!value && !values.casino_contactByEmail) {
                        return [t('contactBySMS.required')];
                    }
                }
            })
        ]
    },
    from: [
        regexp({ re: /^(\d{4})(-)(\d{1,2})(-)(\d{1,2})$/, error: t('from.invalidateDate') }),
        fn({
            fn: (value: string, _: unknown, values: { [key: string]: string }) => {
                if (new Date(value) > new Date(values?.until)) {
                    return [t('from.incorrectValue')];
                }
            }
        })
    ],
    until: [
        regexp({ re: /^(\d{4})(-)(\d{1,2})(-)(\d{1,2})$/, error: t('until.invalidateDate') }),
        fn({
            fn: (value: string) => {
                if (new Date(value) > new Date()) {
                    return [t('until.invalidateDate')];
                }
            }
        })
    ],
    homeVenue: [],
    phone: []
});
